import { permissionSummary } from '../components/PermissionEditor';
import Message from '../components/Message';
import UserGroupForm from '../components/UserGroupForm';
import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Key, Pencil, Plus, X } from 'lucide-react';
import api from '../api/api';
import { Alert, Confirm, Modal, ErrorState, TableSkeleton } from '../components';
import { PageHeader } from '../components/layout';

const emptyNewUser = {
  email: '',
  username: '',
  password: '',
  is_admin: false,
  is_active: true,
  group_id: ''
};

const emptyNewGroup = {
  name: '',
  description: '',
  permissions: [],
  is_active: true
};

const UsersPage = ({ isAdmin = false, canWriteUsers = false }) => {
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [users, setUsers] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState({ modules: [], dependencies: {}, templates: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newUser, setNewUser] = useState(emptyNewUser);
  const [newGroup, setNewGroup] = useState(emptyNewGroup);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });

  const fetchAccessData = useCallback(async () => {
    try {
      setLoading(true);
      const requests = [
        api.getUsers(),
        api.getUserGroups(),
        api.getPermissionCatalog()
      ];
      const [usersResponse, groupsResponse, permissionsResponse] = await Promise.all(requests);
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
      setUserGroups(Array.isArray(groupsResponse.data) ? groupsResponse.data : []);
      setPermissionCatalog(permissionsResponse.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccessData();
  }, [fetchAccessData]);

  const showError = (err, fallback) => {
    setAlertState({ isOpen: true, message: err.response?.data?.detail || err.message || fallback });
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    if (saving) return;
    setCreateError(null);
    setSaving(true);
    try {
      await api.createUser(newUser);
      setNewUser(emptyNewUser);
      setCreateUserOpen(false);
      await fetchAccessData();
      setAlertState({ isOpen: true, message: '用户创建成功' });
    } catch (err) {
      setCreateError(err.response?.data?.detail || err.message || '创建失败');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async (event) => {
    event.preventDefault();
    if (!editingUser) return;
    setSaving(true);
    try {
      const payload = {
        email: editingUser.email,
        username: editingUser.username
      };
      if (isAdmin) {
        payload.is_admin = editingUser.is_admin;
        payload.is_active = editingUser.is_active;
        payload.group_id = editingUser.group_id || null;
      }
      await api.updateUser(editingUser.id, payload);
      setEditingUser(null);
      await fetchAccessData();
      setAlertState({ isOpen: true, message: '用户更新成功' });
    } catch (err) {
      showError(err, '更新用户失败');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateGroup = async (event) => {
    event.preventDefault();
    if (saving) return;
    setCreateError(null);
    setSaving(true);
    try {
      await api.createUserGroup(newGroup);
      setNewGroup(emptyNewGroup);
      setCreateGroupOpen(false);
      setGroupsOpen(true);
      await fetchAccessData();
      setAlertState({ isOpen: true, message: '分组创建成功' });
    } catch (err) {
      setCreateError(err.response?.data?.detail || err.message || '创建失败');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateGroup = async (event) => {
    event.preventDefault();
    if (!editingGroup || saving) return;
    setSaving(true);
    try {
      const original = userGroups.find(group => group.id === editingGroup.id);
      const payload = { name: editingGroup.name, description: editingGroup.description, is_active: editingGroup.is_active };
      if (JSON.stringify([...(editingGroup.permissions || [])].sort()) !== JSON.stringify([...(original?.permissions || [])].sort())) {
        payload.permissions = editingGroup.permissions;
      }
      const response = await api.updateUserGroup(editingGroup.id, payload);
      const updatedGroup = response.data.group;
      setUserGroups(current => current.map(group => group.id === updatedGroup.id ? updatedGroup : group));
      setUsers(current => current.map(user => user.group_id === updatedGroup.id
        ? { ...user, group_name: updatedGroup.name } : user));
      setEditingGroup(null);
      setGroupsOpen(true);
      setAlertState({ isOpen: true, message: '分组更新成功' });
    } catch (err) {
      if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
        setAlertState({ isOpen: true, message: '保存请求超时，结果尚未确认，请刷新页面核对分组后再操作。' });
      } else {
        showError(err, '更新分组失败');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!deletingGroup || saving) return;
    setSaving(true);
    try {
      await api.deleteUserGroup(deletingGroup.id);
      setDeletingGroup(null);
      await fetchAccessData();
      setAlertState({ isOpen: true, message: '分组已删除' });
    } catch (err) {
      showError(err, '删除分组失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser || deleteLoading) return;
    setDeleteLoading(true);
    setDeleteMessage(null);
    try {
      await api.deleteUser(deletingUser.id);
      setUsers(prev => prev.filter(user => user.id !== deletingUser.id));
      setDeletingUser(null);
      setDeleteMessage({ type: 'success', message: '用户已删除' });
    } catch (err) {
      setDeleteMessage({ type: 'error', message: err.response?.data?.detail || err.message || '删除用户失败' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await api.updateUserStatus(user.id, !user.is_active);
      setUsers(prev => prev.map(item => (
        item.id === user.id ? { ...item, is_active: !user.is_active } : item
      )));
    } catch (err) {
      showError(err, '更新用户状态失败');
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    if (!resetUser) return;
    setSaving(true);
    try {
      await api.resetUserPassword(resetUser.id, { new_password: newPassword });
      setResetUser(null);
      setNewPassword('');
      setAlertState({ isOpen: true, message: '密码已重置' });
    } catch (err) {
      showError(err, '重置密码失败');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  };

  if (error) {
    return <ErrorState title="无法加载用户管理" description={error} onRetry={fetchAccessData} />;
  }

  return (
    <div className="configuration-list-page users-directory-page">
      <Message message={deleteMessage?.message} type={deleteMessage?.type} />
      <Confirm isOpen={Boolean(deletingGroup)} title="删除权限分组"
        message={`确定删除“${deletingGroup?.name || ''}”？仍有成员的分组不能删除。`}
        confirmText="删除" loading={saving} loadingText="删除中…"
        onConfirm={handleDeleteGroup} onCancel={() => setDeletingGroup(null)} />
      <Confirm isOpen={Boolean(deletingUser)} title="删除用户"
        message={`确认删除用户 ${deletingUser?.username || ''}？该账号及个人授权将被删除，已有规则和业务记录将保留。`}
        confirmText="删除" loading={deleteLoading} loadingText="删除中…"
        onConfirm={handleDeleteUser} onCancel={() => setDeletingUser(null)} />
      <Message message={createError} type="error" />
      <PageHeader
        title="用户管理"
        actions={<>
          <button type="button" className="btn btn-secondary" onClick={() => setGroupsOpen(true)}>权限分组</button>
          {isAdmin && <button type="button" className="btn btn-primary" onClick={() => { setNewUser(emptyNewUser); setCreateError(null); setCreateUserOpen(true); }}><Plus size={16} aria-hidden="true" />创建用户</button>}
        </>}
      />

      {isAdmin && <Modal isOpen={createUserOpen} title="创建用户" size="large" className="configuration-editor-dialog" busy={saving} onClose={() => setCreateUserOpen(false)}>
        <p className="configuration-dialog-description">创建后用户可使用邮箱和初始密码登录。</p>
        <form onSubmit={handleCreateUser}>
          <fieldset disabled={saving} className="configuration-form-fields">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="newUser-email">邮箱 *</label>
              <input
                id="newUser-email" type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="form-input"
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="newUser-username">用户名 *</label>
              <input
                id="newUser-username" type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                className="form-input"
                minLength="2"
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="newUser-password">初始密码 *</label>
              <input
                id="newUser-password" type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="form-input"
                minLength="6"
                required
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" htmlFor="newUser-group_id">权限分组</label>
              <select
                id="newUser-group_id" value={newUser.group_id}
                onChange={(e) => setNewUser({ ...newUser, group_id: e.target.value ? Number(e.target.value) : '' })}
                className="form-select"
              >
                <option value="">未分组</option>
                {userGroups.map(group => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={newUser.is_admin}
                onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })}
              />
              <span>管理员</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={newUser.is_active}
                onChange={(e) => setNewUser({ ...newUser, is_active: e.target.checked })}
              />
              <span>启用</span>
            </label>
          </div>
        </fieldset>

          <div className="configuration-dialog-actions">
            <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setCreateUserOpen(false)}>取消</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '创建中…' : '创建用户'}</button>
          </div>
        </form>
      </Modal>}

      <Modal isOpen={groupsOpen && !deletingGroup} title="权限分组" size="large" className="configuration-editor-dialog" busy={saving} onClose={() => setGroupsOpen(false)}>
        <div className="configuration-dialog-toolbar">
          <p className="configuration-dialog-description">共 {userGroups.length} 个分组，管理菜单与接口权限。</p>
          {isAdmin && <button type="button" className="btn btn-primary" onClick={() => { setGroupsOpen(false); setNewGroup(emptyNewGroup); setCreateError(null); setCreateGroupOpen(true); }}><Plus size={16} aria-hidden="true" />新建权限分组</button>}
        </div>
                <div className="user-group-directory">
          {!loading && userGroups.length === 0 && <div className="empty-state">暂无权限分组</div>}
          {userGroups.map(group => (
            <article key={group.id} className="user-group-record">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{group.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>{group.description || '-'}</div>
                </div>
                <span className={`badge ${group.is_active ? 'badge-success' : 'badge-default'}`}>
                  {group.is_active ? '启用' : '禁用'}
                </span>
              </div>
              <div style={{ marginTop: 'var(--space-3)', color: 'var(--text-secondary)', fontSize: '12px' }}>
                {permissionSummary(group.permissions, permissionCatalog.modules)}
              </div>
              {isAdmin && <div className="table-action-icon-list" style={{ marginTop: 'var(--space-3)' }}>
                <button type="button" className="table-action-icon-button" onClick={() => { setGroupsOpen(false); setEditingGroup({ ...group }); }} aria-label="编辑权限分组" title="编辑权限分组"><Pencil size={17} aria-hidden="true" /></button>
                <button type="button" className="table-action-icon-button" aria-label="复制权限分组" title="复制权限分组" onClick={() => {
                  setNewGroup({ ...emptyNewGroup, name: `${group.name}副本`, description: group.description || '', permissions: group.permissions || [] });
                  setCreateError(null); setGroupsOpen(false); setCreateGroupOpen(true);
                }}><Copy size={17} aria-hidden="true" /></button>
                {!group.is_system && (
                  <button type="button" className="table-action-icon-button is-danger" onClick={() => setDeletingGroup(group)} aria-label="删除权限分组" title="删除权限分组"><X size={19} aria-hidden="true" /></button>
                )}
              </div>}
            </article>
          ))}
        </div>

      </Modal>

      {isAdmin && <Modal isOpen={createGroupOpen} title="新建权限分组" size="large" className="configuration-editor-dialog user-group-editor-dialog" busy={saving}
        onClose={() => { setCreateGroupOpen(false); setGroupsOpen(true); }}>
        <UserGroupForm value={newGroup} onChange={setNewGroup} catalog={permissionCatalog}
          saving={saving} onSubmit={handleCreateGroup} isNew
          onCancel={() => { setCreateGroupOpen(false); setGroupsOpen(true); }} />
      </Modal>}

      <section className="configuration-list-surface users-directory-surface">
        <header className="configuration-list-surface-header">
          <div>
            <h2>用户列表</h2>
            <p>共 {users.length} 位用户，禁用账号会阻止其继续登录。</p>
          </div>
        </header>
        <div className="configuration-list-surface-body">
        {loading ? (
          <TableSkeleton rowCount={5} columnCount={7} label="加载用户列表中" />
        ) : (
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '70px' }}>ID</th>
                  <th>用户</th>
                  <th>角色</th>
                  <th>权限分组</th>
                  <th>Jira 配置</th>
                  <th>创建时间</th>
                  <th style={{ width: '100px' }}>状态</th>
                  <th style={{ width: '190px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: 'var(--space-10)' }}>暂无用户</td>
                  </tr>
                ) : users.map(user => (
                  <tr key={user.id}>
                    <td>#{user.id}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{user.username}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '2px' }}>{user.email}</div>
                    </td>
                    <td>
                      <span className={`badge ${user.is_admin ? 'badge-info' : 'badge-default'}`}>
                        {user.is_admin ? '管理员' : '普通用户'}
                      </span>
                    </td>
                    <td>{user.group_name || <span style={{ color: 'var(--text-muted)' }}>未分组</span>}

                    </td>
                    <td>{user.has_jira_config ? <span className="badge badge-success">已配置</span> : <span style={{ color: 'var(--text-muted)' }}>未配置</span>}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{formatDate(user.created_at)}</td>
                    <td>
                      {isAdmin ? (
                        <label className="switch" title={user.is_active ? '点击禁用' : '点击启用'}>
                          <input
                            type="checkbox"
                            checked={user.is_active}
                            onChange={() => handleToggleStatus(user)}
                          />
                          <span className="switch-slider"></span>
                        </label>
                      ) : (
                        <span className={`badge ${user.is_active ? 'badge-success' : 'badge-default'}`}>
                          {user.is_active ? '启用' : '禁用'}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="table-action-icon-list">
                        {canWriteUsers && (
                          <button type="button" className="table-action-icon-button" onClick={() => setEditingUser({ ...user })} aria-label="编辑用户" title="编辑用户"><Pencil size={17} aria-hidden="true" /></button>
                        )}
                        {isAdmin && (
                          <button type="button" className="table-action-icon-button" onClick={() => setResetUser(user)} aria-label="重置密码" title="重置密码"><Key size={17} aria-hidden="true" /></button>
                        )}
                        {isAdmin && <button type="button" className="table-action-icon-button is-danger" onClick={() => setDeletingUser(user)} aria-label="删除用户" title="删除用户"><X size={19} aria-hidden="true" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </section>

      {editingUser && (
        <Modal isOpen title="编辑用户" className="configuration-editor-dialog" busy={saving} onClose={() => { setEditingUser(null); }}>
            <form onSubmit={handleUpdateUser}>
              <fieldset disabled={saving} className="configuration-form-fields">
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label" htmlFor="editingUser-email">邮箱</label>
                  <input
                    id="editingUser-email" type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="editingUser-username">用户名</label>
                  <input
                    id="editingUser-username" type="text"
                    value={editingUser.username}
                    onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                    className="form-input"
                    minLength="2"
                    required
                  />
                </div>
                {isAdmin && <div className="form-group">
                  <label className="form-label" htmlFor="editingUser-group_id">权限分组</label>
                  <select
                    id="editingUser-group_id" value={editingUser.group_id || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, group_id: e.target.value ? Number(e.target.value) : null })}
                    className="form-select"
                  >
                    <option value="">未分组</option>
                    {userGroups.map(group => (
                      <option key={group.id} value={group.id}>{group.name}</option>
                    ))}
                  </select>
                </div>}
                {isAdmin && <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editingUser.is_admin}
                      onChange={(e) => setEditingUser({ ...editingUser, is_admin: e.target.checked })}
                    />
                    <span>管理员</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editingUser.is_active}
                      onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.checked })}
                    />
                    <span>启用</span>
                  </label>
                </div>}
              </div>
              </fieldset>
              <div className="configuration-dialog-actions">
                <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setEditingUser(null)}>取消</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '保存中...' : '保存修改'}</button>
              </div>
            </form>
        </Modal>
      )}

      {isAdmin && editingGroup && (
        <Modal isOpen title="编辑权限分组" size="large" className="configuration-editor-dialog user-group-editor-dialog" busy={saving} onClose={() => { setEditingGroup(null); setGroupsOpen(true); }}>
          <UserGroupForm value={editingGroup} onChange={setEditingGroup} catalog={permissionCatalog}
            saving={saving} onSubmit={handleUpdateGroup}
            onCancel={() => { setEditingGroup(null); setGroupsOpen(true); }} />
        </Modal>
      )}

      {isAdmin && resetUser && (
        <Modal isOpen title="重置密码" className="configuration-editor-dialog" busy={saving} onClose={() => { setResetUser(null); }}>
            <form onSubmit={handleResetPassword}>
              <fieldset disabled={saving} className="configuration-form-fields">
              <div className="modal-body">
                <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
                  正在为 {resetUser.username} 重置登录密码。
                </p>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">新密码</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="form-input"
                    minLength="6"
                    required
                  />
                </div>
              </div>
              </fieldset>
              <div className="configuration-dialog-actions">
                <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setResetUser(null)}>取消</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? '重置中...' : '确认重置'}</button>
              </div>
            </form>
        </Modal>
      )}

      <Alert
        isOpen={alertState.isOpen}
        title="提示"
        message={alertState.message}
        onClose={() => setAlertState({ isOpen: false, message: '' })}
      />
    </div>
  );
};

export default UsersPage;
